import { useQuery } from '@tanstack/react-query';
import { useServices } from '@/contexts/app-services';
import type { Goal } from '@/entities/goal/types';
import type { Plan } from '@/entities/plan/types';
import { getSortedPlannedGoals } from '@/entities/planned-goal/operations';
import type { PlannedGoal } from '@/entities/planned-goal/types';
import type { Period } from '@/entities/types';
import { useGoalsByIds } from '@/queries/goal';
import { useOrCreatePlan } from '@/queries/plan';
import { plannedGoalsQueryOptions } from '@/queries/planned-goal';

interface CompletePlanGoal {
  goalId: string; // goal.id
  plannedGoalId: string; // plannedGoal.id
  position: number; // plannedGoal.priority
  title: string; // goal.title
  status: 'open' | 'done'; // goal.status
}

export interface CompletePlan extends Pick<
  Plan,
  'id' | 'period' | 'startAtLocal' | 'startAt'
> {
  goals: CompletePlanGoal[];
}

function toCompletePlan(
  plan: Plan,
  plannedGoals: PlannedGoal[],
  goals: Goal[],
): CompletePlan {
  const goalMap = new Map(goals.map((goal) => [goal.id, goal]));

  const sortedPlannedGoals = getSortedPlannedGoals(plannedGoals);

  const mergedGoals = sortedPlannedGoals
    .map((plannedGoal, index) => {
      const goal = goalMap.get(plannedGoal.goalId);
      if (!goal) {
        return null;
      }

      return {
        goalId: goal.id,
        plannedGoalId: plannedGoal.id,
        position: index,
        title: goal.title,
        status: goal.status,
      };
    })
    .filter((goal): goal is CompletePlanGoal => goal !== null);

  return {
    id: plan.id,
    period: plan.period,
    startAtLocal: plan.startAtLocal,
    startAt: plan.startAt,
    goals: mergedGoals,
  };
}

export function useCompletePlan(
  period: Period,
  startAt: Date,
  startAtLocal: string,
) {
  const { api } = useServices();

  // Step 1: Get or create the plan
  const {
    plan,
    isLoading: planLoading,
    error: planError,
  } = useOrCreatePlan(period, startAt, startAtLocal);

  // Step 2: Get planned goals (dependent on having a plan)
  const plannedGoalsQuery = useQuery({
    ...plannedGoalsQueryOptions(plan?.id ?? '', api),
    enabled: !!plan?.id,
  });

  // Step 3: Get individual goals (reuse existing hook that handles server + client goals)
  const goalIds = plannedGoalsQuery.data?.map((pg) => pg.goalId) || [];
  const goalsQuery = useGoalsByIds(goalIds, {
    source: 'useCompletePlan',
    planId: plan?.id,
  });

  // Calculate loading/error states
  const isLoading =
    planLoading ||
    plannedGoalsQuery.isLoading ||
    goalsQuery.goals === undefined;
  const error =
    planError ||
    plannedGoalsQuery.error ||
    (goalsQuery.isError ? goalsQuery.error : null);

  // Transform data if not loading and we have the required data
  const completePlan =
    !isLoading && plan && plannedGoalsQuery.data && goalsQuery.goals
      ? toCompletePlan(plan, plannedGoalsQuery.data, goalsQuery.goals)
      : undefined;

  return {
    completePlan,
    isLoading,
    error,
  };
}
